/**
 * Application Configuration Constants
 * General app-level settings and configuration
 */

/**
 * Application environment and build configuration
 */
export const APP_CONFIG = {
    VERSION: '1.0.0',
    BUILD_DATE: new Date().toISOString().split('T')[0],
    ENVIRONMENT: process.env.NODE_ENV || 'development',
    DEBUG_MODE: process.env.NODE_ENV === 'development',
    API_BASE_URL: process.env.REACT_APP_API_URL || '',
    ENABLE_ANALYTICS: process.env.REACT_APP_ENABLE_ANALYTICS === 'true'
};

/**
 * Performance and optimization settings
 */
export const PERFORMANCE_CONFIG = {
    CALCULATION_DEBOUNCE_MS: 300,
    AUTO_SAVE_INTERVAL_MS: 30000, // 30 seconds
    MAX_CALCULATION_ITERATIONS: 100,
    CALCULATION_TOLERANCE: 1e-7,
    ENABLE_WORKER_CALCULATIONS: false, // For future web worker implementation
    MAX_HISTORY_ENTRIES: 50
};

/**
 * Feature flags for enabling/disabling functionality
 */
export const FEATURE_FLAGS = {
    ENABLE_GOAL_SEEK: true,
    ENABLE_IMPORT_EXPORT: true,
    ENABLE_PRINT_PDF: true,
    ENABLE_DARK_MODE: true,
    ENABLE_BESS_ANALYSIS: true,
    ENABLE_SENSITIVITY_ANALYSIS: true,
    ENABLE_MONTE_CARLO: true,
    ENABLE_MACHINE_LEARNING: true,
    ENABLE_CLIMATE_RISK: true,
    ENABLE_PORTFOLIO_ANALYSIS: true,
    ENABLE_ADVANCED_ANALYTICS: true
};

/**
 * Calculation and financial model settings
 */
export const CALCULATION_CONFIG = {
    DEFAULT_CURRENCY: 'USD',
    CURRENCY_SYMBOL: '$',
    DECIMAL_PLACES: {
        CURRENCY: 0,
        PERCENTAGE: 1,
        RATE: 4,
        YEARS: 1,
        TECHNICAL: 3
    },
    GOAL_SEEK: {
        MAX_ITERATIONS: 100,
        TOLERANCE: 1e-5,
        MIN_FUNDS: 1,
        MAX_FUNDS_MULTIPLIER: 1.1,
        CONVERGENCE_THRESHOLD: 0.01
    },
    IRR_CALCULATION: {
        MAX_ITERATIONS: 100,
        TOLERANCE: 1e-7,
        INITIAL_GUESS: 0.1
    }
};

/**
 * Validation and input constraints
 */
export const VALIDATION_CONFIG = {
    ENABLE_REAL_TIME_VALIDATION: true,
    SHOW_VALIDATION_WARNINGS: true,
    STRICT_BOUNDS_CHECKING: true,
    AUTO_CORRECT_INVALID_INPUTS: false,
    MAX_INPUT_LENGTH: 20,
    ENABLE_BUSINESS_RULES_VALIDATION: true
};

/**
 * Logging and monitoring configuration
 */
export const LOGGING_CONFIG = {
    ENABLE_CONSOLE_LOGGING: APP_CONFIG.DEBUG_MODE,
    ENABLE_FILE_LOGGING: false,
    LOG_LEVEL: APP_CONFIG.DEBUG_MODE ? 'debug' : 'warn',
    MAX_LOG_ENTRIES: 1000,
    LOG_CALCULATION_PERFORMANCE: APP_CONFIG.DEBUG_MODE,
    LOG_USER_INTERACTIONS: false
};

/**
 * Regional settings and localization
 */
export const REGIONAL_CONFIG = {
    DEFAULT_REGION: 'PHILIPPINES_VISAYAS',
    SUPPORTED_REGIONS: [
        'PHILIPPINES_VISAYAS',
        'PHILIPPINES_LUZON', 
        'PHILIPPINES_MINDANAO'
    ],
    DEFAULT_TIMEZONE: 'Asia/Manila',
    DEFAULT_LOCALE: 'en-PH',
    NUMBER_FORMAT: 'en-US' // For consistent number formatting
};

/**
 * Browser and device compatibility settings
 */
export const COMPATIBILITY_CONFIG = {
    MIN_SUPPORTED_BROWSERS: {
        Chrome: 90,
        Firefox: 88,
        Safari: 14,
        Edge: 90
    },
    ENABLE_LEGACY_SUPPORT: false,
    ENABLE_MOBILE_OPTIMIZATIONS: true,
    ENABLE_TOUCH_GESTURES: true,
    PREFERRED_THEME: 'system' // 'light', 'dark', or 'system'
};

/**
 * Data storage and persistence settings
 */
export const STORAGE_CONFIG = {
    ENABLE_LOCAL_STORAGE: true,
    ENABLE_SESSION_STORAGE: true,
    AUTO_SAVE_INPUTS: true,
    PERSIST_UI_STATE: true,
    STORAGE_KEY_PREFIX: 'solar_bess_model_',
    COMPRESS_STORED_DATA: false,
    ENCRYPTION_ENABLED: false
};

/**
 * Export and import settings
 */
export const EXPORT_CONFIG = {
    DEFAULT_FORMATS: ['CSV', 'JSON'],
    INCLUDE_METADATA: true,
    INCLUDE_CALCULATIONS: true,
    INCLUDE_CHARTS_DATA: false,
    COMPRESS_EXPORTS: false,
    MAX_EXPORT_SIZE_MB: 10
};

/**
 * Print and PDF generation settings
 */
export const PDF_CONFIG = {
    DEFAULT_ORIENTATION: 'portrait',
    DEFAULT_FORMAT: 'letter',
    INCLUDE_CHARTS: false, // Charts are complex to render in PDF
    INCLUDE_ALL_TABS: true,
    EXCLUDE_TABS: ['README'],
    OPTIMIZE_FOR_PRINT: true,
    INCLUDE_PAGE_NUMBERS: true,
    INCLUDE_GENERATION_DATE: true
}; 